import React, { useState, useEffect } from 'react';
import { EIN_REGEX, formatEIN } from '../utils/ein';
import { CONTACT_PHONE } from '../config';

const FIELDS = [
  { key: 'name',         label: 'Full Name',     type: 'text',  placeholder: 'Jane Smith'      },
  { key: 'businessName', label: 'Business Name', type: 'text',  placeholder: 'Acme Hardware'   },
  { key: 'email',        label: 'Email Address', type: 'email', placeholder: 'jane@example.com' },
  { key: 'phone',        label: 'Phone Number',  type: 'tel',   placeholder: '(828) 555-0100'  },
];

// Single source for the business/organization label so the visible label and
// its validation message can never drift apart.
function orgNameLabel(isNonprofit) {
  return isNonprofit ? 'Organization Name' : 'Business Name';
}

function validate(contact, isNonprofit, ein) {
  const errors = {};
  if (!contact.name.trim())         errors.name = 'Full name is required.';
  if (!contact.businessName.trim()) errors.businessName = `${orgNameLabel(isNonprofit)} is required.`;
  if (!contact.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!contact.phone.trim()) errors.phone = 'Phone number is required.';
  if (isNonprofit && !EIN_REGEX.test(ein || '')) errors.ein = true;
  return errors;
}

export default function Step4Contact({ form, setForm, onChangeOrgType, onNext, onBack }) {
  const [errors,   setErrors]   = useState({});
  const [touched,  setTouched]  = useState({});
  const [switched, setSwitched] = useState(false);

  const isNonprofit = form.orgType === 'nonprofit';

  // When a non-profit arrives with the contact fields already filled (the
  // review-step "switch to discounted rates" detour), land focus on the EIN
  // field — it's the only thing left to do and it's below the fold of an
  // otherwise complete-looking form.
  useEffect(() => {
    const c = form.contact;
    const filled = c.name.trim() && c.businessName.trim() && c.email.trim() && c.phone.trim();
    if (isNonprofit && filled && !EIN_REGEX.test(form.ein || '')) {
      const el = document.getElementById('ein');
      if (el) el.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update(key, value) {
    setForm(f => ({ ...f, contact: { ...f.contact, [key]: value } }));
    if (touched[key]) {
      const errs = validate({ ...form.contact, [key]: value }, isNonprofit, form.ein);
      setErrors(prev => ({ ...prev, [key]: errs[key] }));
    }
  }

  function blur(key) {
    setTouched(t => ({ ...t, [key]: true }));
    const errs = validate(form.contact, isNonprofit, form.ein);
    setErrors(prev => ({ ...prev, [key]: errs[key] }));
  }

  // EIN validates on Continue only — a blur-time error would grow the field's
  // cell and shift the escape link mid-click, swallowing the click.
  function handleEINChange(e) {
    setForm(f => ({ ...f, ein: formatEIN(e.target.value) }));
    setErrors(prev => (prev.ein ? { ...prev, ein: undefined } : prev));
  }

  function switchToBusiness() {
    onChangeOrgType('business', 'contact_ein');
    setErrors(prev => ({
      ...prev,
      ein: undefined,
      // Re-word any stale "Organization Name is required." under the relabeled field.
      businessName: prev.businessName ? `${orgNameLabel(false)} is required.` : prev.businessName,
    }));
    setSwitched(true);
    // The clicked link unmounts with the EIN block; park focus somewhere sensible.
    setTimeout(() => {
      const el = document.getElementById('businessName');
      if (el) el.focus();
    }, 0);
  }

  function handleNext() {
    const allTouched = Object.fromEntries(FIELDS.map(f => [f.key, true]));
    setTouched(allTouched);
    const errs = validate(form.contact, isNonprofit, form.ein);
    setErrors(errs);
    if (Object.keys(errs).length === 0) onNext();
  }

  return (
    <div className="step-card">
      <h2 className="step-heading">You're almost done</h2>
      <p className="step-subheading">
        Enter your contact info below and we'll handle the rest.
      </p>

      <div className="contact-grid">
        {FIELDS.map(f => {
          const label = f.key === 'businessName' ? orgNameLabel(isNonprofit) : f.label;
          return (
            <div key={f.key} className="contact-field">
              <label htmlFor={f.key}>
                {label} <span className="required-star">*</span>
              </label>
              <input
                id={f.key}
                type={f.type}
                placeholder={f.placeholder}
                value={form.contact[f.key] || ''}
                onChange={e => update(f.key, e.target.value)}
                onBlur={() => blur(f.key)}
                className={errors[f.key] ? 'error' : ''}
                autoComplete={
                  f.key === 'name'         ? 'name'         :
                  f.key === 'businessName' ? 'organization' :
                  f.key === 'email'        ? 'email'        : 'tel'
                }
              />
              {errors[f.key] && (
                <span className="field-error">{errors[f.key]}</span>
              )}
            </div>
          );
        })}

        {/* EIN — collected here (not at step 1) so non-profits see prices before
            digging up paperwork. Still required before the review/payment step. */}
        {isNonprofit && (
          <div className="contact-field">
            <label htmlFor="ein">
              Non-profit EIN (federal tax ID) <span className="required-star">*</span>
            </label>
            <input
              id="ein"
              type="text"
              inputMode="numeric"
              placeholder="XX-XXXXXXX"
              maxLength={10}
              value={form.ein || ''}
              onChange={handleEINChange}
              className={errors.ein ? 'error' : ''}
              aria-invalid={errors.ein ? true : undefined}
              aria-describedby="ein-help"
              autoComplete="off"
            />
            {errors.ein ? (
              <span className="field-error" id="ein-help" role="alert">
                Enter your 9-digit EIN as XX-XXXXXXX (e.g. 56-1234567). Can't find it?
                Call <a href={CONTACT_PHONE.tel}>{CONTACT_PHONE.display}</a> and we'll finish your order together.
              </span>
            ) : (
              <span className="field-hint" id="ein-help">
                It's on your IRS determination letter &mdash; we use it once to confirm your non-profit rate.
              </span>
            )}
            <button type="button" className="text-link-btn" onClick={switchToBusiness}>
              Not a 501(c)(3)? Switch to business rates
            </button>
          </div>
        )}
      </div>

      <div aria-live="polite">
        {switched && !isNonprofit && (
          <p className="field-hint" style={{ marginTop: 10 }}>
            Business rates applied &mdash; no EIN needed.
          </p>
        )}
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={onBack}>&larr; Back</button>
        <button className="btn btn-primary"    onClick={handleNext}>Continue &rarr;</button>
      </div>
    </div>
  );
}
